import { useState, useEffect } from "react";

interface FloatingGiftCardProps {
  onClose: () => void;
}

interface NoButtonPos {
  x: number;
  y: number;
}

export default function FloatingGiftCard({ onClose }: FloatingGiftCardProps) {
  const [particles, setParticles] = useState<
    Array<{ id: number; left: number; top: number; delay: number }>
  >([]);
  const [noButtonPos, setNoButtonPos] = useState<NoButtonPos>({ x: 0, y: 0 });

  useEffect(() => {
    // Generate floating particles
    const newParticles = Array.from({ length: 15 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      delay: Math.random() * 0.5,
    }));
    setParticles(newParticles);

    // Initial position for No button - next to the Yes button
    // Position 0, 0 means it's relative (inside card)
    setNoButtonPos({ x: 0, y: 0 });
  }, []);

  const randomizeNoButtonPosition = () => {
    // Keep button within screen boundaries with padding
    // Button is approximately 150px wide and 50px tall
    const buttonWidth = 150;
    const buttonHeight = 50;
    const padding = 20;

    const maxX = Math.max(padding, window.innerWidth - buttonWidth - padding);
    const maxY = Math.max(padding, window.innerHeight - buttonHeight - padding);

    const x = Math.random() * maxX;
    const y = Math.random() * maxY;

    setNoButtonPos({ x, y });
  };

  const handleNoHover = () => {
    randomizeNoButtonPosition();
  };

  const promiseMessage = `A little promise,
from my heart to yours. That I'll keep choosing you, supporting you, laughing with you, and loving you through every chapter of our story.

Thank you for being my safe place, my biggest supporter, and my favorite person. I can't wait to make more memories, chase more sunsets, and build a beautiful future together.

Will you grow old with me? ❤️`;

  return (
    <>
      {/* Backdrop with blur */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(63, 0, 0, 0.6)",
          backdropFilter: "blur(8px)",
          zIndex: 98,
          animation: "fadeInBackdrop 0.5s ease-out",
        }}
        onClick={onClose}
      />

      {/* Centered Fancy Card */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "450px",
          background:
            "linear-gradient(135deg, #ffeaa7 0%, #c67c84 50%, #fa6363 100%)",
          borderRadius: "30px",
          padding: "50px 40px",
          zIndex: 999,
          animation: "popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
          boxShadow:
            "0 25px 50px rgba(255, 102, 117, 0.4), 0 0 100px rgba(255, 168, 150, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
          border: "3px solid rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(10px)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Floating particles */}
        {particles.map((p) => (
          <div
            key={p.id}
            style={{
              position: "absolute",
              width: `${4 + Math.random() * 6}px`,
              height: `${4 + Math.random() * 6}px`,
              backgroundColor: `rgba(255, 255, 255, ${0.5 + Math.random() * 0.5})`,
              borderRadius: "50%",
              left: `${p.left}%`,
              top: `${p.top}%`,
              animation: `twinkle ${2 + p.id * 0.2}s ease-in-out infinite`,
              animationDelay: `${p.delay}s`,
              filter: "blur(0.5px)",
            }}
          />
        ))}

        {/* Decorative top ornament */}
        <div
          style={{
            position: "absolute",
            top: "15px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "40px",
            animation: "bounce 2.5s ease-in-out infinite",
          }}
        >
          ✨
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "rgba(255, 255, 255, 0.2)",
            border: "2px solid rgba(255, 255, 255, 0.5)",
            fontSize: "24px",
            cursor: "pointer",
            padding: "0 6px",
            color: "#fff",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.3s ease",
            backdropFilter: "blur(5px)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.3)";
            e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.8)";
            e.currentTarget.style.transform = "scale(1.1) rotate(90deg)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
            e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.5)";
            e.currentTarget.style.transform = "scale(1) rotate(0deg)";
          }}
        >
          ✕
        </button>

        {/* Main content */}
        <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          {/* Title */}
          <h1
            style={{
              margin: "0 0 30px 0",
              fontSize: "36px",
              fontWeight: "800",
              color: "#fff",
              textShadow: "0 3px 10px rgba(0, 0, 0, 0.2)",
              letterSpacing: "1px",
              animation: "fadeInUp 0.8s ease-out 0.2s both",
            }}
          >
            One Last Question... 💖
          </h1>

          {/* Message */}
          <div
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              padding: "25px 20px",
              borderRadius: "20px",
              marginBottom: "30px",
              backdropFilter: "blur(5px)",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              animation: "fadeInUp 0.8s ease-out 0.4s both",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "18px",
                color: "#fff",
                lineHeight: "1.8",
                whiteSpace: "pre-line",
                fontWeight: "500",
                textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              {promiseMessage}
            </p>
          </div>

          {/* Action Buttons - Side by Side */}
          <div
            style={{
              display: "flex",
              gap: "20px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: "14px 40px",
                border: "2px solid rgba(255, 255, 255, 0.6)",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "#fff",
                borderRadius: "25px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "700",
                transition: "all 0.3s ease",
                backdropFilter: "blur(5px)",
                letterSpacing: "0.5px",
                animation: "fadeInUp 0.8s ease-out 0.6s both",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(255, 255, 255, 0.3)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 1)";
                e.currentTarget.style.transform =
                  "scale(1.05) translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 10px 25px rgba(255, 100, 100, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(255, 255, 255, 0.2)";
                e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.6)";
                e.currentTarget.style.transform = "scale(1) translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              Yes! 💕
            </button>

            {/* Floating "No" Button - Inside card initially, escapes on hover */}
            {noButtonPos.x === 0 && (
              <button
                onMouseEnter={handleNoHover}
                onTouchStart={handleNoHover}
                style={{
                  padding: "14px 40px",
                  border: "2px solid rgba(255, 255, 255, 0.6)",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "#fff",
                  borderRadius: "25px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "700",
                  transition: "all 0.2s ease",
                  backdropFilter: "blur(5px)",
                  letterSpacing: "0.5px",
                  animation: "fadeInUp 0.8s ease-out 0.7s both",
                  position: "static",
                }}
              >
                No ❌
              </button>
            )}
          </div>

          {/* Decorative bottom element */}
          <div
            style={{
              position: "absolute",
              bottom: "10px",
              left: "5%",
              transform: "translateX(-50%)",
              fontSize: "20px",
              animation: "float 3s ease-in-out infinite",
            }}
          >
            💍
          </div>
        </div>
      </div>

      {/* Floating "No" Button - Rendered outside card to escape transform context */}
      {noButtonPos.x !== 0 && (
        <button
          onMouseEnter={handleNoHover}
          onTouchStart={handleNoHover}
          style={{
            position: "fixed",
            left: `${noButtonPos.x}px`,
            top: `${noButtonPos.y}px`,
            padding: "14px 40px",
            border: "2px solid rgba(255, 100, 100, 0.6)",
            backgroundColor: "rgba(255, 100, 100, 0.2)",
            color: "#ff6464",
            borderRadius: "25px",
            cursor: "not-allowed",
            fontSize: "16px",
            fontWeight: "700",
            transition: "all 0.2s ease",
            backdropFilter: "blur(5px)",
            letterSpacing: "0.5px",
            zIndex: 9999,
          }}
        >
          No ❌
        </button>
      )}

      <style>{`
        @keyframes popIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.5) rotateX(-20deg);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1) rotateX(0deg);
          }
        }

        @keyframes fadeInBackdrop {
          from {
            opacity: 0;
            backdropFilter: blur(0px);
          }
          to {
            opacity: 1;
            backdropFilter: blur(8px);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes twinkle {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.2);
          }
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          50% {
            transform: translateX(-50%) translateY(-15px);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          50% {
            transform: translateX(-50%) translateY(-10px);
          }
        }
      `}</style>
    </>
  );
}
